import { type JanusCtx, type Row, activeStatuses, handBack, int, itemField, record, requiredInt, upper, definitionNotHeld } from './context.js';
import type { AdvDisTraceEntry } from '../actions/weapon-attack-commit.js';
import { readNpcSkillModifier } from '../compile/from-base-npc-skill-sources.js';

const SKILL_ESSENCE: Record<string, string> = {
  '59C08B6D-F8D4-F9DB-81D9-0FD592094C73': 'body', // Balance
  '24C07918-39D5-4317-A958-BDF68B633A21': 'body', // Fortitude
  '58A62523-2588-452B-876B-61D8ACF3CCED': 'mind', // Psyche
  '922C5882-54DE-42D0-A5C8-8118AA1C858E': 'soul', // Willpower
  'F964C0DA-54EA-1EB5-ED00-8E9AD50FFA53': 'spellcasting_essence', // Bending Weapon Skill
  'D7F51670-F6D9-5DD6-11D9-0CDBF35096A0': 'soul', // Machining
  'ABE36CBD-1142-9809-FA05-C7DC4263B43C': 'mind', // Magic (Skill::ALL essence_used = Essence::CORE[1] — TRAPS 2026-09-28)
  '45DE7B42-D548-498A-B7AB-C3DC1C660E57': 'body', // Natural Weapon Skill
  '850BDFE9-D36A-46AF-AD28-E1C533E140E3': 'body', // Firearms Weapon Skill
  '6E1A31A4-2D30-096B-BEA4-E2A1DC93A583': 'body', // Initiative (Skill::ALL essence_used = Essence::CORE[0] — lane A4 for N5b's combat start)
  // Lane A8 (2026-10-02, template-skill-check.ts): the rest of Skill::ALL's essence_used (tools/probes/janus_a8_skill_essences.php)
  'F99172CC-DDEF-48D4-87F8-8B94F0071247': 'soul', // Attention
  '397C35B1-76DB-4A45-1468-E53CF797A1A3': 'soul', // Empathy
  '30735770-7440-0C26-5FDF-86C452C0C91D': 'body', // Frighten
  'F5A23631-F770-1C49-79F0-E8A2B7A6DCAC': 'soul', // Influence
  '28E0D03E-86B3-FDF4-E03A-8DFA1043FE3E': 'mind', // Inspection
  '64493E62-BCAF-27F1-2B6A-EC84DE328F66': 'body', // Sneak
  '9F177F51-CDAC-0012-A574-DE1B8B17A55F': 'body', // Excavating
  'B3856A19-34AD-D33A-14F1-BBBE79A67DA5': 'body', // Farming
  '59B5B7EA-38B8-28C7-B74F-182906B4E3D7': 'body', // Fishing
  '42D2DDEC-9C0C-E111-BE72-F556B47EDBFE': 'body', // Herbalism
  '964A6407-E371-E6FA-A3FC-4ECDAD3615B7': 'body', // Hunting
  '1E875D11-8C3F-53D6-5256-7FF5363FB1E4': 'body', // Locks
  'B6B008F2-5805-C31F-842F-ED0C16D34484': 'body', // Mining
  '114E1810-D8A6-72C2-54B8-7EDBC5D43A57': 'body', // Skinning
  '27C6E09D-9CD0-41E5-B765-18F096CD8781': 'mind', // Architecture
  '432EF6B0-0DF5-5EA9-33A6-F1BE691772D0': 'mind', // Astronomy
  'AFF19B47-5E29-B9EA-1CEF-D017C9F440C3': 'mind', // Biology
  '432DBB43-005F-88D9-4F25-B5745D5C08D2': 'mind', // Civics
  'E38D3E9B-55CE-49EE-272B-C053D3C2E3B9': 'mind', // Finance
  '10657AA2-E10A-4C86-A814-B3A24B0CB252': 'mind', // Gamesmanship
  'E023B642-259E-69D7-4E72-334FE0A6EE64': 'mind', // Geology
  '58B3E565-2DAB-C967-766E-7D815EF1429B': 'mind', // History
  'D7F6DF07-296F-B231-3E70-9D6B46575709': 'mind', // Law
  '258BD584-26FB-F656-D15F-566BE8984551': 'mind', // Linguistics
  '761600DE-35EB-7B14-5980-F2430FFF85AB': 'mind', // Medicine
  'F259DADA-89E7-F08F-E24C-DA140F84FB36': 'mind', // Music
  'DEC0A112-9ADC-09D3-0FA7-3A59CFE8C008': 'mind', // Navigation
  '1D2EB61A-67FB-4F6D-FC1B-B5C36357CA6E': 'mind', // Planes
  '4D0FF632-CAC9-4714-64B8-43978DD90A7E': 'mind', // Religion
  '86BD9ED9-4768-239D-E755-BC198290FDBE': 'soul', // Alchemy
  '9CC50F9B-B8AD-B180-79BE-C309F1563800': 'soul', // Art
  '7229802A-2169-DDD4-6CCB-E755CCFAEB51': 'soul', // Cooking
  'E77A8B34-F880-E0E2-E6EC-42F4241F4FCC': 'soul', // Enchanting
  'B27AC929-722C-685E-E93E-CE38F49FF9BE': 'soul', // Glassblowing
  'AFD61488-049C-C7FC-2B72-26CBE7EAEAFD': 'soul', // Jewelling
  'F29D806E-160C-4EC2-923F-AAF1D5CDDB7E': 'soul', // Leatherworking
  '6C3E6964-F138-CA3F-DB99-4BFC7AF3E4FB': 'soul', // Masoning
  '0A68ECAB-D224-CF73-DB51-761BCFD663FA': 'soul', // Smithing
  '55565C71-B55B-3D10-9DFB-7C45D95BC724': 'soul', // Weaving
  'E5347227-42FD-9173-6C81-ADAD3E0ED6ED': 'soul', // Woodworking
  'A5B19883-CBDA-EC46-A859-96FF01651278': 'mind', // Abolian
  'B12FE4D0-480A-239A-DAF0-704850F5162D': 'mind', // Abyssal
  '820AB0A3-F21A-3135-DB75-89DCDF09A9D0': 'mind', // Aquan
  'B1B5365D-2DE0-AA53-A729-AC28BF8C8D52': 'mind', // Auran
  '81DADED4-7909-482A-BE32-76887642C172': 'mind', // Common
  '6EEF7655-4FC7-F5F4-0AB8-C31C31FEEDCD': 'mind', // Domir
  '0700727F-6C9E-4631-1140-B57A8E442137': 'mind', // Draconic
  '94D1A00F-4795-B1DD-6981-6CE3364AF1B3': 'mind', // Druidic
  '5C346F0F-D58D-2859-92EB-A347FA56899E': 'mind', // Dwarvish
  '42728623-5523-5986-672E-9A9FE5DD35B5': 'mind', // Elvish
  '4A5172E6-6D97-3958-C65B-F0792A660B85': 'mind', // Enochian
  '3098C163-7B26-7757-24F1-129C467D5BDB': 'mind', // Gnomish
  '6AB76ADB-22BC-904A-7D72-43FDCA112DD1': 'mind', // Grel
  '4767BD78-9829-6FC9-BD4A-5855F322CEB7': 'mind', // Ignan
  '22CC3970-0458-63CA-3D69-82A746B9A5CD': 'mind', // Nagh
  '16A53E58-027B-DF11-6AC5-72CA13B6583A': 'mind', // Orcish
  '2CB279E7-CD79-1A21-9F32-89184886D7FC': 'mind', // Primordial
  '641E712F-1B60-C633-14BF-C4EE0DDD4AF4': 'mind', // Qini
  'FBD22CAB-950F-8DAE-E052-65A88BF5205B': 'mind', // Sign Language
  'D267CD08-6952-99E9-B19E-52EC690546BB': 'mind', // Sylvan
  'EAC11C79-B399-7259-8556-8EDA88B7FCD8': 'mind', // Terran
  'E76097FE-D47E-FD76-B7AF-8F0C40A3BCA1': 'mind', // Thieves Cant
  'ABAE4F65-1A42-DEFC-21C8-11EBF883A484': 'mind', // Umbral
  '208E59A5-37B5-4DA3-97BE-8A1F540C94CD': 'soul', // Cymbals Skill
  'F9134996-0370-34A2-A101-13497D6631A0': 'soul', // Drums Skill
  '1DB53BE8-AB8F-CEB7-57E9-A3162F2AE4B3': 'soul', // Flutes Skill
  '3C5D9464-C206-44E7-015F-77A700D9BD9B': 'soul', // Harps Skill
  '3B5FFB0D-26EF-B237-1F54-63CFF4004E02': 'soul', // Keyboards Skill
  '7D2FFF31-8526-6AC0-42A7-BBCEBA432DBE': 'soul', // Horns Skill
  '937D4758-2CD8-1DA4-E47D-7E0DFD4E9C06': 'soul', // Pipes Skill
  '1D54B02A-B9BF-B47F-A22B-25182CE551F0': 'soul', // Stringed, Bow Skill
  '182CA00C-D9E4-C2AA-A243-B4EB4B8977B7': 'soul', // Stringed, Plucked Skill
  'F7FCBD40-CA99-88E6-BC5B-7D2F7D3C0EDB': 'soul', // Zithers Skill
  '7BB6F92D-9C9F-D3D1-80FF-BBDBA3D06017': 'body', // One-Handed Melee Weapon Skill
  '8F94D454-48C2-A031-AED0-943ED5B609AF': 'body', // Two-Handed Melee Weapon Skill
  '2A16EE98-E160-89CD-4AC0-529BF36194D2': 'body', // Archery Weapon Skill
  '1A3033B2-1FA4-485A-9D0A-1CD0BC92C961': 'body', // Throwable Weapon Skill
  '937A5402-E933-4F87-8CAE-93427F47894A': 'body', // Heavy Armor Skill
  '44F9404E-77AC-1F60-68A4-4CD6AE071161': 'body', // Light Armor Skill
  '24495AD8-8EB9-488E-B816-5513E0A3DAA8': 'body', // Medium Armor Skill
  '71E4E5C5-7AF6-9B16-90BF-8D9D6CCFD9DC': 'body', // Natural Armor Skill
  'E973D6BE-E84E-4373-B134-BA8AA13BCD30': 'body', // Shield Armor Skill
};

export interface SelectedSkill { id: string; tier: number; adv: number; modifier: number; compiledAdvKnown: boolean;
  compiledAdvSources: ReadonlySet<string>;
  compiledAdvSourceValues: ReadonlyMap<string, number>;
  compiledModKnown?: boolean;
  compiledModSources?: ReadonlySet<string>;
  compiledModSourceValues?: ReadonlyMap<string, number>;
  advDisTrace?: readonly AdvDisTraceEntry[];
  essenceMod: number; essenceName: string; allSkillsMod: number; skillRollMod: number;
  /** build_caster_skills' compiled `forced_natural_main_roll` (skill_is_rolled_at_max → 20 / _min → 1, max wins): skill_roll_arr sets the
   *  main d20's natural to it instead of rolling (2026-10-07 — was a hand-back). null = an ordinary roll. */
  forcedNatural?: 1 | 20 | null }

/** `bookkeepingOnly` (summons_gain_caster_cantrips — rollerSkillBookkeeping): the ROLLER's ADV / DIS accounting for a skill whose numbers
 *  come from another creature; its essence is not read (a summon may carry no spellcasting essence) and reads 0. */
function skillDetails(ctx: JanusCtx, creature: Row, id: string, bookkeepingOnly = false): SelectedSkill {
  const definition = ctx.read.definition(id);
  const essence = SKILL_ESSENCE[id];
  if (!definition || !essence) handBack(`select_skill_for_contesting_roll:unknown-skill:${!definition ? 'definition-not-held' : 'essence-unmapped'}:${id}`);
  const variable = String(itemField(definition, 'item_name') ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const computed = record(creature.computed);
  const creatureData = record(computed?.creature);
  const skills = record(creatureData?.skills);
  if (!skills) handBack('select_skill_for_contesting_roll:projection-not-held');
  const entry = record(skills?.[variable]);
  // build_caster_skills supplies an Untrained row when an allowed skill is absent
  // from the compiled skill bucket (e.g. Machining on a PC).
  const tier = entry ? requiredInt(entry.tier_level, 'select_skill_for_contesting_roll:invalid-tier') : 0;
  if (tier < 0 || tier > 20) handBack('skill_roll_arr:tier-out-of-range');
  // forced_natural_main_roll (2026-10-07, Master of Deception): PHP's `$forced_nat === 1 || $forced_nat === 20` — a strict INT compare, so
  // only an integer 1 / 20 forces; anything else present is a shape the page cannot prove → PHP.
  const forcedRaw = entry?.forced_natural_main_roll;
  const forcedNatural: 1 | 20 | null = forcedRaw === 1 || forcedRaw === 20 ? forcedRaw : null;
  if (forcedNatural === null && forcedRaw != null && forcedRaw !== 0 && forcedRaw !== '0')
    handBack('skill_roll_arr:unmodelled:forced_natural_main_roll');
  const advLines = record(entry?.adv_dis_breakdown)?.lines;
  const modLines = record(entry?.skill_roll_mod_breakdown)?.lines;
  const skillRollMod = entry?.skill_roll_mod == null ? 0 : requiredInt(entry.skill_roll_mod, 'select_skill_for_contesting_roll:invalid-skill-mod');
  const compiledModKnown = entry != null && (modLines === undefined ? skillRollMod === 0
    : Array.isArray(modLines) && modLines.reduce((sum: number, line) => {
      const value = record(line)?.value;
      return sum + (value != null && Number.isSafeInteger(Number(value)) && upper(record(line)?.href_id) ? Number(value) : NaN);
    }, 0) === skillRollMod);
  // The compiled Willpower breakdown is trusted like every other skill's (Session 1 step 6, 2026-09-29). A former special case
  // subtracted Ogrefolk Origins' −1 line as "stale": it was compensating for the contestee's projected contesting_roll_adv_vs_status_effect
  // (Iron Resolve, computed.creature.defenses — now counted in skill-roll.ts), and PHP's own traces keep the line live
  // (AO-274 Power Word: Dread, AO-32 Fleshdobbin Shriek: −1 Ogrefolk + 1 Iron Resolve = one d20).
  const staleWillpower = 0;
  const compiledDie = record(entry?.roll)?.die;
  if (tier > 0 && compiledDie != null && int(compiledDie) !== tier + 3)
    handBack('skill_roll_arr:unmodelled:compiled-tier-die');
  for (const key of ['skill_roll_adv', 'skill_roll_dis', 'skill_roll_mod', 'skill_is_rolled_at_max',
    'skill_is_rolled_at_min', 'adv_dis_stacks'])
    if (ctx.read.has(definition, key)) handBack(`skill_roll_arr:unmodelled:skill.${key}`);
  // PHP skill_roll_arr and skill_expected_roll_value explicitly default an absent
  // compiled ADV/DIS cell to zero; a present null or malformed cell is unknown.
  let adv = (entry?.adv_dis_stacks === undefined ? 0
    : requiredInt(entry.adv_dis_stacks, 'select_skill_for_contesting_roll:invalid-adv')) - staleWillpower - (tier === 0 ? 1 : 0);
  // The GLOBAL all-skill ADV/DIS (skill_rolls_adv / skill_rolls_dis — a held Favor of the Gods, an "all skills" enchantment). skill_roll_arr
  // (skill_roll_inc.php ~L182 / ~L289) replays the compiled breakdown lines WITHOUT the global ones (global_layer, or a legacy
  // 'Global — ' label; no lines → the net minus the root globals) and then adds the ROOT globals once — PHP's root is a FRESH compile.
  // The page's compiled entry can predate a status the page already holds (the status arrives as a typed delta; the compile does not —
  // TRAPS smoke 2026-09-28: Favor held, Machining entry without its line), so the root is rebuilt from what the page holds
  // authoritatively: every HELD status's own skill_rolls_adv/dis + the compiled global lines of every OTHER source. A status added or
  // removed by THIS use is not in PHP's compile either (skill_roll_new_trigger_status_global_adv_dis handles new ones) → excluded /
  // unknown. (TRAPS 2026-09-28 — was a hand-back.)
  const held = ctx.read.statuses(creature);
  const heldIds = new Set(held.map((status) => upper(status.status_id)).filter(Boolean));
  const advDisTrace: AdvDisTraceEntry[] = [];
  const trace = (delta: number, source: string, provider: Row | null, href: string, line?: Row): void => {
    if (!delta) return;
    advDisTrace.push({ delta, source, label: String(line?.label ?? itemField(provider, 'item_name') ?? 'Skill Base'),
      kind: String(line?.kind ?? itemField(provider, 'item_type') ?? 'adv_dis'), ...(href ? { item_id: href } : {}),
      origin_name: String(itemField(definition, 'item_name') ?? ''), origin_item_id: id });
  };
  for (const raw of Array.isArray(advLines) ? advLines : []) {
    const line = record(raw), href = upper(line?.href_id);
    // Global status contributions below use the same live values as the numeric roll, once.
    if (line?.global_layer && heldIds.has(href)) continue;
    const status = held.find(row => upper(row.status_id) === href);
    const provider = status ? ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr) : ctx.read.definition(href);
    trace(int(line?.value), 'skill_base', provider, href, line ?? undefined);
  }
  const globalLines = (key: string): number => (Array.isArray(advLines) ? advLines : []).reduce((sum: number, line) => {
    const row = record(line);
    if (String(row?.global_layer ?? '') !== key || heldIds.has(upper(row?.href_id))) return sum;
    return sum + (row?.value == null ? 0 : requiredInt(row.value, 'select_skill_for_contesting_roll:invalid-adv-breakdown'));
  }, 0);
  let statusAdv = 0, statusDis = 0;
  for (const status of held) {
    const definition = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!definition) definitionNotHeld('skill_roll_arr:global-adv-dis-status-definition-not-held', status.status_item_id);
    const add = (key: string): number => {
      const value = ctx.read.value(definition!, key);
      if (value == null || value === '' || value === 0 || value === '0' || value === false) return 0;
      if (int(status.status_stack ?? 1) !== 1) handBack('skill_roll_arr:global-adv-dis-stacked-status');
      if (ctx.statusesRemoved.some((change) => upper(change.status_item_id) === upper(status.status_item_id)))
        handBack('skill_roll_arr:global-adv-dis-status-removed-this-use');
      return requiredInt(value, 'skill_roll_arr:invalid-global-adv-dis');
    };
    const adv = add('skill_rolls_adv'), dis = add('skill_rolls_dis');
    statusAdv += adv; statusDis += dis;
    trace(adv, 'skill_rolls_adv', definition, upper(status.status_id));
    trace(-dis, 'skill_rolls_dis', definition, upper(status.status_id));
  }
  // Breakdown lines are signed contributions; convert DIS back to a count before subtracting it.
  const globalAdv = statusAdv + globalLines('skill_rolls_adv'), globalDis = statusDis - globalLines('skill_rolls_dis');
  // A legacy cached line ('Global — …' label, no global_layer tag) cannot be attributed to a source or a layer → unknown.
  if ((Array.isArray(advLines) ? advLines : []).some((line) => String(record(line)?.label ?? '').startsWith('Global — ')
      && (record(line)?.global_layer == null || record(line)?.global_layer === '')))
    handBack('skill_roll_arr:global-adv-dis-legacy-line');
  if (globalAdv !== 0 || globalDis !== 0) {
    const net = entry?.adv_dis_stacks === undefined ? 0 : requiredInt(entry.adv_dis_stacks, 'select_skill_for_contesting_roll:invalid-adv');
    const lines = Array.isArray(advLines) ? advLines : [];
    // A net with no breakdown: PHP subtracts ITS root globals — known only when the compile carries every held source.
    if (net !== 0 && !lines.length) handBack('skill_roll_arr:global-adv-dis-net-without-breakdown');
    const baseLines = lines.reduce((sum: number, line) => {
      const row = record(line);
      const value = row?.value == null ? 0 : requiredInt(row.value, 'select_skill_for_contesting_roll:invalid-adv-breakdown');
      if (value === 0 || (row?.global_layer != null && row.global_layer !== '' && row.global_layer !== 0)) return sum;
      return sum + value;
    }, 0);
    // A zero net still contains every signed source, including non-global lines that cancel the globals.
    adv = baseLines - staleWillpower - (tier === 0 ? 1 : 0) + globalAdv - globalDis;
  }
  const essences = record(creatureData?.essences);
  const spellcasting = record(creatureData?.spellcasting);
  // The compile elides zero. Validate held values before provider fallback; layer-only readers may omit the compiled cell.
  if (!bookkeepingOnly && essence === 'spellcasting_essence' && spellcasting?.essence_mod !== undefined)
    requiredInt(spellcasting.essence_mod, 'select_skill_for_contesting_roll:invalid-essence');
  const essenceValue = bookkeepingOnly ? 0 : essence === 'spellcasting_essence'
    ? requiredInt(spellcasting?.essence_mod === undefined ? 0 : Array.isArray(spellcasting.essence_mod)
      ? spellcasting.essence_mod : ctx.read.value(creature, 'spellcasting_essence_mod') ?? spellcasting.essence_mod, 'select_skill_for_contesting_roll:invalid-essence')
    : requiredInt(record(essences?.[essence])?.total, 'select_skill_for_contesting_roll:invalid-essence');
  const essenceId = upper(ctx.read.value(creature, 'spellcasting_essence') ?? spellcasting?.essence);
  const essenceName = essence === 'spellcasting_essence'
    ? ({ '22918408-67CC-4093-80E8-BF6FA8915C21': 'Body', '63ABC96E-3804-4E1F-B38D-AABA3237B2D8': 'Mind',
      '72F93730-134F-4CA2-8FC4-F2915F20CAC6': 'Soul' }[essenceId] ?? '')
    : essence.charAt(0).toUpperCase() + essence.slice(1);
  const modifier = essenceValue + (entry?.mod === undefined ? skillRollMod + (entry?.skill_rolls_mod == null ? 0
    : requiredInt(entry.skill_rolls_mod, 'select_skill_for_contesting_roll:invalid-all-skills-mod'))
    : requiredInt(entry.mod, 'select_skill_for_contesting_roll:invalid-mod'));
  // The lean compile ELIDES a zero net (`adv_dis_stacks` absent — "absent reads as 0", like PHP's default above): an entry whose breakdown lines
  // sum to 0 is a KNOWN zero, so a source those lines name is verified (lane A4, R-100: the "to death" Fortitude entry carries Exhausted −1 +
  // a +1 passive line and no net). A present null, or lines that do not sum to 0 under an absent net, stay unknown.
  const elidedZeroNet = entry != null && entry.adv_dis_stacks === undefined
    // (Codex P3) a line without an integer value is malformed → NaN → unknown (never read as 0)
    && (Array.isArray(advLines) ? advLines : []).reduce((sum: number, line) => { const v = record(line)?.value; return sum + (v != null && Number.isInteger(Number(v)) ? Number(v) : NaN); }, 0) === 0;
  return { id, tier, adv, modifier, advDisTrace, compiledAdvKnown: entry?.adv_dis_stacks != null || elidedZeroNet,
    compiledModKnown,
    compiledModSources: new Set(Array.isArray(modLines) ? modLines.map(line => upper(record(line)?.href_id)).filter(Boolean) : []),
    compiledModSourceValues: new Map(Array.isArray(modLines) ? [...new Set(modLines.map(line => upper(record(line)?.href_id)).filter(Boolean))]
      .map(source => [source, modLines.filter(line => upper(record(line)?.href_id) === source)
        .reduce((sum, line) => sum + Number(record(line)?.value), 0)]) : []),
    compiledAdvSources: new Set(Array.isArray(advLines) ? advLines.map((line) => upper(record(line)?.href_id)).filter(Boolean) : []),
    compiledAdvSourceValues: new Map(Array.isArray(advLines) ? [...new Set(advLines.map((line) => upper(record(line)?.href_id)).filter(Boolean))]
      .map((source) => [source, advLines.filter((line) => upper(record(line)?.href_id) === source)
        .reduce((sum, line) => sum + requiredInt(record(line)?.value, 'select_skill_for_contesting_roll:invalid-adv-breakdown'), 0)]) : []),
    essenceMod: essenceValue, essenceName, allSkillsMod: entry?.skill_rolls_mod == null ? 0 : requiredInt(entry.skill_rolls_mod, 'select_skill_for_contesting_roll:invalid-all-skills-mod'),
    skillRollMod,
    forcedNatural };
}

/** includes/functions/janus/core/select_skill_for_contesting_roll.php:select_skill_for_contesting_roll */
export function selectSkillForContestingRoll(ctx: JanusCtx, creature: Row, entries: unknown[]): SelectedSkill {
  const ids = entries.map((entry) => upper(typeof entry === 'string' ? entry.split('|')[0] : itemField(record(entry), 'item_id')));
  if (ids.some((id) => !/^[0-9A-F-]{36}$/.test(id))) handBack('select_skill_for_contesting_roll:invalid-skill-entry');
  if (!ids.length) return skillDetails(ctx, creature, 'F964C0DA-54EA-1EB5-ED00-8E9AD50FFA53');
  let best: SelectedSkill | null = null;
  let bestEv = -Infinity;
  const candidates = new Map<string, SelectedSkill>();
  for (const id of ids) {
    const skill = skillDetails(ctx, creature, id);
    candidates.set(id, skill);
    const dieAverage = skill.tier > 0 ? (skill.tier + 4) / 2 : 0;
    const advValue = skill.adv === 0 ? 0 : Math.sign(skill.adv) * (3.5 + (Math.abs(skill.adv) - 1) * 1.6);
    const ev = 10.5 + skill.modifier + dieAverage + advValue;
    if (ev > bestEv) { best = skill; bestEv = ev; }
  }
  if (!best) handBack('select_skill_for_contesting_roll:no-valid-candidate');
  // PHP rebuilds stale compiled entries before ranking. A source id alone
  // does not prove that the cached contribution still has the live value.
  let liveStatusScoped = false;
  const expectedBySource = new Map<string, Map<string, number>>();
  if (ids.length > 1) for (const status of activeStatuses(ctx, creature)) {
    const definition = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!definition) definitionNotHeld('select_skill_for_contesting_roll:status-definition-not-held', status.status_item_id);
    for (const entry of ctx.read.entries(definition, 'skill_roll_mod')) {
      const mod = readNpcSkillModifier(entry, (row, key) => ctx.read.value(record(row), key));
      const candidate = candidates.get(upper(mod.skill));
      if (candidate && (!candidate.compiledModKnown || !candidate.compiledModSources?.has(upper(status.status_id))))
        handBack('select_skill_for_contesting_roll:live-status-winner-uncertain');
    }
    for (const key of ['skill_roll_adv', 'skill_roll_dis']) for (const entry of ctx.read.entries(definition, key)) {
      const id = upper(itemField(record(entry), 'item_id') ?? entry);
      if (!candidates.has(id)) continue;
      liveStatusScoped = true;
      const source = upper(status.status_id);
      if (!source) handBack('select_skill_for_contesting_roll:live-status-winner-uncertain');
      const bySkill = expectedBySource.get(source) ?? new Map<string, number>();
      bySkill.set(id, (bySkill.get(id) ?? 0) + (key === 'skill_roll_adv' ? 1 : -1));
      expectedBySource.set(source, bySkill);
    }
  }
  for (const [source, bySkill] of expectedBySource) for (const [id, expected] of bySkill) {
    const candidate = candidates.get(id)!;
    if (!candidate.compiledAdvSources.has(source) || candidate.compiledAdvSourceValues.get(source) !== expected)
      handBack('select_skill_for_contesting_roll:live-status-winner-uncertain');
  }
  if (liveStatusScoped && best.id !== ids[0]) handBack('select_skill_for_contesting_roll:live-status-winner-uncertain');
  return best;
}

/** summons_gain_caster_cantrips (Wizard v9): the ROLLER's own ADV / DIS bookkeeping for skill `id` (its compiled entry's adv_dis_stacks and
 *  breakdown sources, its held statuses' all-skill globals) — the pair skillRollArr's guardRollLayers vets the roller's statuses against.
 *  The skill's NUMBERS (tier, essence, mods) come from the stat creature (summon-caster-cantrips.ts selectCastingSkill). */
export function rollerSkillBookkeeping(ctx: JanusCtx, roller: Row, id: string): SelectedSkill {
  return skillDetails(ctx, roller, id, true);
}
